import classnames from "classnames";
import flatten from "lodash.flatten";
import fromPairs from "lodash.frompairs";
import uniqBy from "lodash.uniqby";
import React, { ReactNode, useState } from "react";
import Aside from "../../components/Aside/Aside";
import PlainButton from "../../components/Button/PlainButton/PlainButton";
import CampaignRandom from "../../components/CampaignRandom/CampaignRandom";
import { Col, Row, Wrapper } from "../../components/Grid";
import IsDesktop from "../../components/IsDesktop/IsDesktop";
import Text from "../Text/Text";
import GetText from "../../i18n/GetText";
import { useTranslationStrings } from "../../i18n/TranslationStringsContext";
import {
  SanityCampaign,
  SanityDocument,
  SanityRawContent,
  SanityThirdPartyResources
} from "../../model/common";
import { isValidAgeRange } from "../../utils/age-range-util";
import { isValidLevel } from "../../utils/level-util";
import FilterAside from "../FilterAside/FilterAside";
import Tag from "../Tag/Tag";
import Tags from "../Tags/Tags";
import ThirdPartyResources from "../ThirdPartyResources/ThirdPartyResources";
import {
  applyFilters,
  applyQuery,
  calculateFilterCounts,
  FilterType,
  ListingConfig
} from "./filters";
import styles from "./Listing.module.scss";
import { useFilterSelectionState } from "./useFilterSelectionState";

const Listing = <T extends SanityDocument>({
  campaigns,
  title,
  sideBarText,
  thirdPartyResourcesTitleId,
  config,
  contents,
  renderRows,
  page,
  thirdPartyResources
}: ListingProps<T>) => {
  const translationStrings = useTranslationStrings();
  const filtersByConfigId = fromPairs(
    config.filters.map(c => [
      c.id,
      uniqBy(flatten(contents.map(item => c.values(item))), item => item._id)
    ])
  );
  const allFilters = flatten(Object.values(filtersByConfigId));

  // Perhaps this shouldn't clear age/level on desktop?
  const clearAll = allFilters.reduce((result, item) => {
    result[item._id] = false;
    return result;
  }, {} as Record<string, boolean>);

  const filterState = useFilterSelectionState();
  const { filterSelection, isSelected, setSelected } = filterState;
  const [query, setQuery] = useState("");

  const filteredContents = applyQuery(
    config.searchFields,
    query,
    applyFilters(config, filtersByConfigId, isSelected, contents)
  );
  const filterCounts = calculateFilterCounts(config, filteredContents);
  return (
    <Wrapper className={styles.listingWrapper}>
      <Row className={styles.listingContainer}>
        <Col xs={12} sm={4} md={3} as={"aside"}>
          <FilterAside
            filterState={filterState}
            filterCounts={filterCounts}
            filtersByConfigId={filtersByConfigId}
            config={config}
            title={title}
            sideBarText={sideBarText}
            query={query}
            setQuery={setQuery}
          />
        </Col>
        <Col xs={"auto"} sm={8} as={"main"} className={styles.mainCol}>
          <Row style={{ marginTop: "30px" }}>
            {allFilters && (
              <>
                <Col xs={12}>
                  <IsDesktop>
                    {config.filters
                      .filter(c => c.location === "top")
                      .map(c =>
                        c.component({
                          isSelected,
                          setSelected,
                          filterCounts,
                          titleId: c.titleId,
                          inputNamePrefix: "desktop",
                          filterData: filtersByConfigId[c.id]
                        })
                      )}
                  </IsDesktop>
                </Col>
                <Col xs={12} className={styles.tagsColumn}>
                  <Text
                    variant="defaultBold"
                    as="span"
                    className={styles.listingFilterCount}
                  >
                    {filteredContents.length}{" "}
                    <GetText
                      id={filteredContents.length === 1 ? "result" : "results"}
                    />
                  </Text>
                  {(filterSelection.length > 0 || query.length > 0) && (
                    <>
                      <Tags truncateAt={3}>
                        {allFilters
                          .filter(isSelected)
                          .map((filter: FilterType) => (
                            <Tag
                              mobileOnly={
                                isValidAgeRange(filter._id) ||
                                isValidLevel(filter._id)
                              }
                              key={filter._id}
                              onClick={() =>
                                setSelected({ [filter._id]: false })
                              }
                              text={
                                translationStrings[filter.name] || filter.name
                              }
                            />
                          ))}
                      </Tags>
                      <PlainButton
                        onClick={() => {
                          setSelected(clearAll);
                          setQuery("");
                        }}
                        className={styles.clearButton}
                      >
                        <Text variant="default" as="span">
                          <GetText id={"clear-all"} />
                        </Text>
                      </PlainButton>
                    </>
                  )}
                </Col>
                <Col xs={12} md={"auto"} className={styles.resultsArea}>
                  <>
                    {allFilters &&
                      renderRows({
                        results: filteredContents,
                        filters: filterState.filterSelection,
                        page
                      })}
                  </>
                </Col>
              </>
            )}
            <Col className={styles.rightAside} xs={"auto"} md={3}>
              <Aside sticky={false}>
                {thirdPartyResources && thirdPartyResourcesTitleId && (
                  <ThirdPartyResources
                    title={<GetText id={thirdPartyResourcesTitleId} />}
                    resources={thirdPartyResources}
                  />
                )}
                <CampaignRandom
                  className={classnames(styles.campaign, styles.vertical)}
                  campaigns={campaigns}
                  orientation="vertical"
                />
                <CampaignRandom
                  className={classnames(styles.campaign, styles.horizontal)}
                  campaigns={campaigns}
                  orientation="horizontal"
                />
              </Aside>
            </Col>
          </Row>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default Listing;

export interface ListedItemTextIds {
  singular: string;
  lesson: string;
  thirdPartyResourcesTitle: string;
}

export interface ListingProps<T> {
  campaigns: SanityCampaign[];
  title: string;
  sideBarText?: SanityRawContent;
  config: ListingConfig<T>;
  contents: T[];
  page: SanityDocument;
  renderRows: (props: {
    results: T[];
    filters: string[];
    page: SanityDocument;
  }) => ReactNode;
  thirdPartyResourcesTitleId?: string;
  thirdPartyResources?: SanityThirdPartyResources[];
}
